/**
 * Utility functions for device tracking and status calculations
 */

/**
 * Calculate on-track status for individual modalities
 * Compares latest reading timestamp against current time
 * 
 * @param {Array} readings - Array of device readings with completedDate and modalityType
 * @param {Object} schedule - Device schedule object with modality frequencies
 * @returns {Object} - Object with modality names as keys and their on-track status
 */
export const calculateModalityTrackingStatus = (readings, schedule) => {
  if (!readings || readings.length < 1 || !schedule) {
    return {};
  }

  const currentTime = new Date();

  // Map modality types to schedule keys and frequencies
  const modalityConfig = {
    'ecg': { scheduleKey: 'ecgMins', frequency: schedule.ecgMins },
    'ppg': { scheduleKey: 'ppgMins', frequency: schedule.ppgMins },
    'ausc': { scheduleKey: 'audioMins', frequency: schedule.audioMins },
    'Temperature': { scheduleKey: 'tempMins', frequency: schedule.tempMins },
    'bioz': { scheduleKey: 'biozMins', frequency: schedule.biozMins }
  };

  const modalityStatus = {};

  // Process each modality type
  Object.entries(modalityConfig).forEach(([modalityType, config]) => {
    const frequency = config.frequency;
    
    // Skip if no frequency configured
    if (!frequency || frequency <= 0) {
      return;
    }

    // Filter readings for this specific modality type
    const modalityReadings = readings.filter(r => r.modalityType === modalityType);

    // Need at least 1 reading to compare against current time
    if (modalityReadings.length < 1) {
      modalityStatus[config.scheduleKey] = {
        isOnTrack: false, // Mark as off track if no data
        hasData: false
      };
      return;
    }

    // Sort by completedDate descending to get the latest reading
    const sortedReadings = [...modalityReadings].sort((a, b) => 
      new Date(b.completedDate) - new Date(a.completedDate)
    );

    const latestReading = sortedReadings[0];

    // Calculate time since last reading in minutes
    const timeDeltaMs = currentTime - new Date(latestReading.completedDate);
    const timeDeltaMinutes = timeDeltaMs / (1000 * 60);

    // Add 1 minute margin to frequency
    const isOnTrack = timeDeltaMinutes <= (frequency + 1);

    modalityStatus[config.scheduleKey] = {
      isOnTrack,
      hasData: true,
      timeDelta: timeDeltaMinutes,
      frequency
    };
  });

  return modalityStatus;
};

/**
 * Calculate if device is on track based on latest reading compared to current time
 * 
 * @param {Array} readings - Array of device readings with completedDate property
 * @param {Object} schedule - Device schedule object with modality frequencies (ecgMins, ppgMins, etc.)
 * @returns {boolean} - True if device is on track, false otherwise
 */
export const calculateDeviceOnTrackStatus = (readings, schedule) => {
  if (!readings || readings.length < 1 || !schedule) {
    return true; // Default to on track if we don't have enough data
  }

  const currentTime = new Date();

  // Sort readings by completedDate descending to get the latest
  const sortedReadings = [...readings].sort((a, b) => 
    new Date(b.completedDate) - new Date(a.completedDate)
  );

  const latestReading = sortedReadings[0];

  // Calculate time since last reading in minutes
  const timeDeltaMs = currentTime - new Date(latestReading.completedDate);
  const timeDeltaMinutes = timeDeltaMs / (1000 * 60);

  // Find the fastest (minimum) modality frequency from device schedule
  const modalityFrequencies = [
    schedule.ecgMins,
    schedule.ppgMins,
    schedule.audioMins,
    schedule.tempMins,
    schedule.biozMins
  ].filter(freq => freq != null && freq > 0); // Filter out null/undefined/zero values

  if (modalityFrequencies.length === 0) {
    return true; // No valid frequencies, default to on track
  }

  const fastestFrequency = Math.min(...modalityFrequencies);

  // Device is on track if the time since last reading is within the fastest frequency plus 1 minute margin
  return timeDeltaMinutes <= (fastestFrequency + 1);
};

/**
 * Fetch latest recording timestamp and on-track status from device readings
 * 
 * @param {number} patientId - The patient/account ID
 * @param {Object} httpService - The axios HTTP service instance
 * @param {Object} patchMetadata - The patch metadata containing device schedule (optional)
 * @returns {Promise<Object>} - Object containing latestTimestamp, isOnTrack, and hasMetadata
 */
export const fetchDeviceTrackingData = async (patientId, httpService, patchMetadata = null) => {
  if (!patientId) {
    return { latestTimestamp: null, isOnTrack: true, hasMetadata: false };
  }

  // Check if we have valid metadata with device schedule
  const hasMetadata = !!(patchMetadata?.deviceSchedule);

  try {
    // Use the latest readings endpoint for better performance
    const response = await httpService.get(`device/readings/latest/${patientId}`);
    
    if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
      const readings = response.data.data;
      
      // Sort readings by completedDate descending
      const sortedReadings = [...readings].sort((a, b) => 
        new Date(b.completedDate) - new Date(a.completedDate)
      );

      const latestTimestamp = sortedReadings[0]?.completedDate || null;

      // Calculate on track status if patchMetadata is available
      let isOnTrack = true;
      let modalityStatus = {};
      if (hasMetadata) {
        isOnTrack = calculateDeviceOnTrackStatus(readings, patchMetadata.deviceSchedule);
        modalityStatus = calculateModalityTrackingStatus(readings, patchMetadata.deviceSchedule);
      }

      return { latestTimestamp, isOnTrack, modalityStatus, readings, hasMetadata };
    }

    return { latestTimestamp: null, isOnTrack: true, modalityStatus: {}, readings: [], hasMetadata };
  } catch (err) {
    console.error('Error fetching device tracking data:', err);
    return { latestTimestamp: null, isOnTrack: true, modalityStatus: {}, readings: [], hasMetadata };
  }
};

